import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Copy, Monitor } from "lucide-react";
import type { NeedsInputRequest } from "@mycompbox/shared";

/**
 * The full "the build needs something from you" request (ENG-340, epic
 * ENG-E63). Everything here comes from the agent, so it is untrusted: it is
 * rendered as plain text only (no HTML, no auto-linking). Steps sit in code
 * blocks with a copy button so the owner can paste them into a terminal.
 */
export function NeedsInputCard({ request }: { request: NeedsInputRequest }) {
  const options = request.options ?? [];
  const steps = request.steps ?? [];
  const ownerItems = request.ownerItems ?? [];
  const ownerTaskIds = request.ownerTaskIds ?? [];
  const anyOnMachine = options.some((o) => o.onMachine);

  return (
    <div className="space-y-4 text-sm" data-testid="needs-input-card">
      <div>
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          The build needs your answer
        </h2>
        <p className="mt-1 whitespace-pre-wrap break-words font-medium text-foreground">
          {request.question}
        </p>
        {request.why && (
          <p className="mt-2 whitespace-pre-wrap break-words text-muted-foreground">
            <span className="font-medium text-foreground">Why: </span>
            {request.why}
          </p>
        )}
      </div>

      {steps.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground">How to find it</h3>
          <StepList steps={steps} />
        </div>
      )}

      {options.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground">
            Ways to answer (quickest first)
          </h3>
          <ol className="mt-2 space-y-3 list-none p-0">
            {options.map((option, i) => (
              <li key={i} className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">
                    {i + 1}. {option.label}
                  </span>
                  {option.onMachine && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2 py-0.5 text-xs text-sky-800 dark:bg-sky-900/30 dark:text-sky-300">
                      <Monitor className="h-3 w-3" />
                      On your computer
                    </span>
                  )}
                </div>
                {option.steps.length > 0 && <StepList steps={option.steps} />}
              </li>
            ))}
          </ol>
          {anyOnMachine && (
            <p className="mt-2 text-xs text-muted-foreground">
              Suggested by the agent — check before running.
            </p>
          )}
        </div>
      )}

      {request.afterAnswer && (
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground">What happens next</h3>
          <p className="mt-1 whitespace-pre-wrap break-words">{request.afterAnswer}</p>
        </div>
      )}

      {(ownerItems.length > 0 || ownerTaskIds.length > 0) && (
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground">Things only you can do</h3>
          {ownerItems.length > 0 && (
            <ul className="mt-1 space-y-1 list-disc pl-5">
              {ownerItems.map((item, i) => (
                <li key={i} className="break-words">
                  <span className="font-medium">{item.title}</span>
                  {item.how && <span className="text-muted-foreground"> — {item.how}</span>}
                </li>
              ))}
            </ul>
          )}
          {ownerTaskIds.length > 0 && (
            <p className="mt-1 flex flex-wrap gap-2 text-xs">
              <span className="text-muted-foreground">Tasks:</span>
              {ownerTaskIds.map((id) => (
                <Link
                  key={id}
                  to={`/tasks/${encodeURIComponent(id)}`}
                  className="text-primary hover:underline"
                >
                  {id}
                </Link>
              ))}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function StepList({ steps }: { steps: string[] }) {
  return (
    <ol className="mt-2 space-y-2 list-none p-0">
      {steps.map((step, i) => (
        <li key={i}>
          <CodeBlock text={step} />
        </li>
      ))}
    </ol>
  );
}

function CodeBlock({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    void (navigator.clipboard as Clipboard | undefined)
      ?.writeText(text)
      .then(() => {
        setCopied(true);
        setTimeout(() => {
          setCopied(false);
        }, 1500);
      })
      .catch(() => undefined);
  };
  return (
    <div className="group relative">
      <pre className="overflow-x-auto whitespace-pre-wrap break-words rounded-md bg-black/30 px-3 py-2 pr-10 font-mono text-xs">
        <code>{text}</code>
      </pre>
      <button
        type="button"
        onClick={handleCopy}
        aria-label={copied ? "Copied" : "Copy"}
        className="absolute right-1.5 top-1.5 rounded p-1 text-muted-foreground hover:bg-white/10 hover:text-foreground"
      >
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
}
